import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GiHamburgerMenu } from 'react-icons/gi';
import { FiLogOut, FiUser, FiChevronDown } from 'react-icons/fi';
import darkLogo from '../Images/darkLogo.png';
import lightLogo from '../Images/lightLogo.png';
import leftArrow from '../Images/arrow-left.svg';
import ThemeToggle from './ThemeToggle.tsx';
import { useNavigate } from 'react-router-dom';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import useAuth from '../hooks/useAuth.ts';
import useTheme from '../hooks/useTheme.ts';
import { useSidebar } from '../context/SidebarProvider.tsx';
import axios from 'axios';
import styles from './Header.module.css';
import Modal from './Modal.tsx';
import { jwtDecode } from 'jwt-decode';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

type DecodedToken = {
  exp: number;
  [key: string]: any;
};

function Header({
  title,
  details,
  back,
  setShowSidebar,
  showSidebar,
}: {
  title?: string;
  details?: boolean;
  back?: boolean;
  setShowSidebar?: (value: boolean) => void;
  showSidebar?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const userMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { name, setAuthenticated } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [logo, setLogo] = useState(theme === 'light' ? lightLogo : darkLogo);

  useEffect(() => {
    setLogo(theme === 'light' ? lightLogo : darkLogo);
  }, [theme]);

  useEffect(() => {
    if (typeof showSidebar === 'boolean') {
      setCollapsed(!showSidebar);
    }
  }, [setCollapsed, showSidebar]);

  const handleLogOutClick = useCallback(async () => {
    setMenuOpen(false);
    localStorage.clear();
    setAuthenticated(false);

    delete axios.defaults.headers.common['Authorization'];

    navigate('/sign-in', { replace: true });
  }, [setAuthenticated, navigate]);

  async function handleRefreshToken() {
    const token = localStorage.getItem('token');
    if (!token) {
      handleLogOutClick();
      return;
    }

    try {
      const response = await axios.post(
        `${backendUrl}/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newToken = response.data.jwt;
      const decoded: DecodedToken = jwtDecode(newToken);
      const exp = decoded.exp;

      localStorage.setItem('token', newToken);
      localStorage.setItem('tokenExp', exp.toString());
      setShowRefreshModal(false);
    } catch (error) {
      console.log('Error refreshing token:', error);
      handleLogOutClick();
    }
  }

  const closeUserMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useOnClickOutside(userMenuWrapRef, closeUserMenu);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        userMenuTriggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const goHome = useCallback(() => {
    navigate('/fleet');
  }, [navigate]);

  const goProfile = useCallback(() => {
    setMenuOpen(false);
    navigate('/profile');
  }, [navigate]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      const tokenExp = localStorage.getItem('tokenExp');

      if (!token || !tokenExp) {
        return;
      }

      const exp = parseInt(tokenExp, 10);
      const currentTime = Math.floor(Date.now() / 1000);

      if (exp < currentTime) {
        console.log('logging out')
        handleLogOutClick();
      } else {
        const timeLeft = exp - currentTime;
        if (timeLeft < 300 && timeLeft > 0) {
          setShowRefreshModal(true);
        }
      }
    };

    checkTokenExpiration();

    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [handleLogOutClick]); 

  return (
    <header className={styles.headingContainer}>
      <div className={styles.headerCellLeft}>
        <div className={styles.logo}>
          <div className={styles.logo_controls}>
            <button
              type="button"
              onClick={() => {
                const nextCollapsed = !collapsed;
                setCollapsed(nextCollapsed);
                if (typeof setShowSidebar === 'function') {
                  setShowSidebar(!nextCollapsed);
                }
              }}
              className={styles.hamburger_menu}
              aria-label="Toggle sidebar"
              aria-expanded={!collapsed}
            >
              <GiHamburgerMenu className={styles.hamburger_icon} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.logo_home}
              onClick={goHome}
              aria-label="Go to home"
            >
              <img src={logo} alt="" className={styles.logo_mark} />
            </button>
          </div>
        </div>
      </div>
      <div className={styles.headerCellCenter}>
        {back && (
          <div className={styles.title_details}>
            <button type="button" className={styles.back_button} onClick={() => navigate(-1)}>
              <img src={leftArrow} alt="" style={{ marginRight: '8px' }} />
            </button>
            <h1 className={styles.heading}>{title}</h1>
          </div>
        )}
        {!back && (
          <div className={styles.title}>
            <h1 className={styles.heading}>{title}</h1>
          </div>
        )}
      </div>
      <div className={`${styles.headerCellRight} ${styles.header_right}`}>
        <ThemeToggle className={styles.header_theme_toggle} />
        <div className={styles.user_menu_wrap} ref={userMenuWrapRef}>
          <button
            type="button"
            ref={userMenuTriggerRef}
            className={styles.user_menu_trigger}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Account menu, ${name?.trim() || 'User'}`}
          >
            <span className={styles.header_avatar} aria-hidden>
              {(name?.trim()?.charAt(0) || 'U').toUpperCase()}
            </span>
            <span className={styles.user_trigger_name}>{name?.trim() || 'User'}</span>
            <FiChevronDown className={styles.user_trigger_chevron} aria-hidden />
          </button>
          {menuOpen && (
            <div className={styles.dropdown_panel} role="menu" aria-label="Account menu">
              <button type="button" role="menuitem" className={styles.dropdown_item} onClick={goProfile}>
                <FiUser className={styles.dropdown_item_icon} aria-hidden />
                <span>Profile</span>
              </button>
              <div className={styles.dropdown_divider} role="separator" />
              <button
                type="button"
                role="menuitem"
                className={`${styles.dropdown_item} ${styles.dropdown_item_danger}`}
                onClick={handleLogOutClick}
              >
                <FiLogOut className={styles.dropdown_item_icon} aria-hidden />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showRefreshModal && (
        <Modal
          open={showRefreshModal}
          setOpen={setShowRefreshModal}
          refreshToken={true}
          onRefreshToken={handleRefreshToken}
          onLogout={handleLogOutClick}
        />
      )}
    </header>
  );
}

export default Header;
